import { useQuery } from 'react-query';

import { apiClient } from 'src/services/api/client';
import { queryKeys } from 'src/services/api/constants';

export const REPLY_SUGGESTIONS_PATH = 'api/landlord/messages/reply-suggestions';

export type IReplySuggestion = {
    id: number;
    question_type: string | null;
    content: string;
};

type ApiResponse = {
    reply_suggestions: IReplySuggestion[];
};

export const getReplySuggestionsQueryKey = (messageUuid: string) => [
    queryKeys.replySuggestions,
    messageUuid,
];

/**
 * Fetch suggested replies for an inbound message. Callers should only enable this
 * once the message has passed the client-side gate (see `passesReplyGate`).
 *
 * Backed by a POST because the first call for a message computes and persists
 * suggestions server-side (an LLM call with side effects), so it is not a safe GET.
 * A capped lead responds 429; we keep `retry: false` so that (and any error) simply
 * leaves `data` undefined and renders nothing — never surfaced to the user.
 */
export const useReplySuggestions = (
    messageUuid: string | undefined,
    enabled = true,
    onError?: (error: unknown) => void
) =>
    useQuery(
        getReplySuggestionsQueryKey(messageUuid || ''),
        async () => {
            const response = await apiClient(REPLY_SUGGESTIONS_PATH, {
                method: 'post',
                json: { message_uuid: messageUuid || '' },
            }).json<ApiResponse>();
            return response.reply_suggestions;
        },
        {
            enabled: enabled && !!messageUuid,
            onError,
            retry: () => false,
        }
    );
